import { useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { CustomBranding, mapCustomBranding } from '../models/User';
import { SkillSubStates } from '../models/SkillState';
import type { StudentSkillStates } from '../models/StudentSkillState';
import type { SkillExitSlipProgress } from '../models/ExitSlip';
import { selectExitSlipSkillProgressFor } from '../redux/reducers/exitSlipSkillProgressSlice';
import { selectSkillEarnedViaExitSlip } from '../redux/reducers/exitSlipEarnedSkillsSlice';
import {
  summarizeSkillExitSlips,
  type SkillExitSlipBadgeSummary,
} from '../businessLogicService/StudentDashboardAux/skillExitSlipProgress';
import { isSkillCompleted } from '../businessLogicService/skillCompletionUtils';
import {
  exitSlipChipLabel,
  isSkillDemonstratedByExitSlips,
  resolveExitSlipSkillMessageVariant,
  type ExitSlipSkillMessageVariant,
} from '../businessLogicService/exitSlipSkillPage';

export interface SkillExitSlipState {
  // Whether there is exit-ticket progress to show: the student is in a Miami-branded network and has
  // finished at least one ticket for this skill. The chip and the ephemeral message gate on this —
  // `inferredViaExitSlip` does not, see below.
  hasTicketProgress: boolean;
  progress: SkillExitSlipProgress | null;
  summary: SkillExitSlipBadgeSummary | null;
  // The chip's words, e.g. "2 Exit tickets done". Null when there is no chip to render.
  chipLabel: string | null;
  // The skill is the student's through exit tickets. True from either source — the substate the
  // Inference Engine wrote, or this device's own reading of the report before it ran — which is what
  // the ticket's "the inferred state arrives from two sides" means in practice.
  //
  // Deliberately not gated on `hasTicketProgress`: the substate arrives with the page's own query,
  // while the report is a second request that lands later. The step the page opens on depends on this
  // answer, so it has to be available on the first render or the student would be moved off the page
  // they are already reading.
  inferredViaExitSlip: boolean;
  messageVariant: ExitSlipSkillMessageVariant | null;
}

/**
 * Nothing to say about exit tickets — what the hook returns outside the Miami-branded experience, and
 * what a surface with no student behind it (the guided-tour demo) passes down in its place.
 */
export const NO_SKILL_EXIT_SLIP_STATE: SkillExitSlipState = {
  hasTicketProgress: false,
  progress: null,
  summary: null,
  chipLabel: null,
  inferredViaExitSlip: false,
  messageVariant: null,
};

/**
 * The substates that mean "this skill was won on exit tickets", as recorded by the backend.
 *
 * Both of them: INFERRED_VIA_EXITSLIP is the inference, PASSED_VIA_EXITSLIP the direct evidence (see
 * the note in mapSkillStatus, which keeps them apart for the rating stars but reads both as Passed).
 * For this page they are the same announcement.
 */
const EXIT_SLIP_EARNED_SUBSTATES: string[] = [SkillSubStates.INFERRED_VIA_EXITSLIP, SkillSubStates.PASSED_VIA_EXITSLIP];

/**
 * Everything /skill/:skillCode shows about this skill's exit tickets, resolved from the three places
 * that know about it so no component has to.
 *
 * The sources, and why all three are read:
 *  - the report in redux (exitSlipSkillProgressSlice), put there by SkillExitSlipReportQuery on this
 *    page — the ticket counts, the fire, and the denominator behind "2/2 Exit tickets",
 *  - the student's skill state from GraphQL — the settled answer once the Inference Engine has run,
 *  - exitSlipEarnedSkillsSlice — the skill the dashboard's result card has just celebrated, which
 *    covers the same SPA session; a reload is covered instead by the report being built with the
 *    pending finish folded in, so the optimistic reading survives it.
 *
 * Gated on Miami branding, the same test UnitSkillBadge applies to the unit header's exit-slip badge:
 * exit tickets only exist in that experience, so everywhere else this hook reports nothing.
 */
export const useSkillExitSlipState = (
  skillCode: string | undefined,
  skillState: StudentSkillStates | null | undefined,
): SkillExitSlipState => {
  const userRedux = useAppSelector(selectUser);
  const skillId = skillState?.skill?.id;
  const progress = useAppSelector(state => selectExitSlipSkillProgressFor(state, skillId));
  const earnedInThisSession = useAppSelector(selectSkillEarnedViaExitSlip(skillId));

  const isMiami =
    !!userRedux?.network?.customBranding &&
    mapCustomBranding[userRedux.network.customBranding] === CustomBranding.MIAMI;

  const substateId = skillState?.skillSubstate?.id ?? '';
  const earnedOnServer = EXIT_SLIP_EARNED_SUBSTATES.includes(substateId);

  return useMemo(() => {
    if (!isMiami || !skillCode) {
      return NO_SKILL_EXIT_SLIP_STATE;
    }

    if (!progress) {
      // Either the report has not landed yet, or this skill has no finished ticket at all — a skill
      // with none has no entry in it (toSkillExitSlipProgress returns null), which is also how a
      // skill no exit ticket infers reads. Nothing to show either way, but the two sources that do
      // not need the report still have their say.
      return { ...NO_SKILL_EXIT_SLIP_STATE, inferredViaExitSlip: earnedOnServer || earnedInThisSession };
    }

    const inferredViaExitSlip = earnedOnServer || earnedInThisSession || isSkillDemonstratedByExitSlips(progress);

    // `skillAlreadyEarned` is what holds back both of the badge's invitations — the fire and the
    // challenge nudge. Passed as the union of every way this skill can already be the student's, so
    // the page cannot invite them to a challenge for a skill they have just demonstrated.
    const alreadyEarned = inferredViaExitSlip || (!!skillState && isSkillCompleted(skillState));
    const summary = summarizeSkillExitSlips(progress, alreadyEarned);

    return {
      hasTicketProgress: true,
      progress,
      summary,
      chipLabel: exitSlipChipLabel(summary),
      inferredViaExitSlip,
      messageVariant: resolveExitSlipSkillMessageVariant(summary, inferredViaExitSlip),
    };
  }, [isMiami, skillCode, progress, earnedOnServer, earnedInThisSession, skillState]);
};
